import React, { useState } from 'react';
import { Send, X, Loader2, Check } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

export default function QuickSMSComposer({ clientId, clientName, initialMessage = '', onClose, onSent }) {
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSend() {
    if (!message.trim()) return;
    setSending(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/comms/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clientId,
          message: message.trim(),
        }),
      });

      if (res.ok) {
        setSent(true);
        if (onSent) onSent();
        setTimeout(() => { if (onClose) onClose(); }, 1500);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to send SMS');
      }
    } catch (err) {
      setError('Network error — could not send');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <Check size={24} className="text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-slate-700">Message sent to {clientName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">Text {clientName}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {error}
            </div>
          )}

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none resize-none"
            placeholder="Type your message..."
            autoFocus
          />

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400">{message.length} characters</span>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!message.trim() || sending}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending...' : 'Send SMS'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
